import { useRef, useMemo } from 'react'
import { parseDate, diffDays, calcRange, buildMonthTicks, buildWeekTicks, GANTT_COLORS } from './ganttUtils'

export interface FaseStrategia {
  id: string
  titolo: string
  ordine: number
  durata_prevista: number
  completata: boolean
  in_ritardo: boolean
  data_inizio_prevista: string | null
  data_fine_prevista: string | null
  partecipanti_ids: string[]
  responsabile_id: string
}
export interface CommessaStrategia {
  id: string
  numero_commessa: string
  titolo: string
  cliente_nome: string
  stato: string
  data_inizio: string | null
  data_prevista_consegna: string | null
  fasi: FaseStrategia[]
}

interface Props {
  commesse: CommessaStrategia[]
  zoom: 'mesi' | 'settimane'
}

const LEFT_W = 260
const ROW_H = 44
const DENSITY_H = 36
const HEADER_H = 48
const today = new Date(); today.setHours(0, 0, 0, 0)

const STATO_COLOR: Record<string, string> = {
  in_esecuzione: '#3b82f6',
  pianificazione: '#94a3b8',
  in_revisione:  '#f59e0b',
  approvata:     '#8b5cf6',
}

export default function GanttCommesse({ commesse, zoom }: Props) {
  const rightRef = useRef<HTMLDivElement>(null)
  const leftRef  = useRef<HTMLDivElement>(null)
  const DAY_W = zoom === 'settimane' ? 28 : 11

  // Date di ogni commessa: da data_inizio a data_prevista_consegna
  // Fallback su date delle fasi se mancano
  const commesseConDate = useMemo(() => commesse.map(c => {
    const faseStart = c.fasi.map(f => f.data_inizio_prevista).filter(Boolean).sort()[0] ?? null
    const faseFine  = c.fasi.map(f => f.data_fine_prevista).filter(Boolean).sort().reverse()[0] ?? null
    const start = c.data_inizio ?? faseStart
    const fine  = c.data_prevista_consegna ?? faseFine
    const giorni = (start && fine)
      ? diffDays(parseDate(start), parseDate(fine)) + 1
      : null
    return { ...c, bar_start: start, bar_fine: fine, giorni_totali: giorni }
  }), [commesse])

  const allDates = useMemo(() => commesseConDate.flatMap(c => [c.bar_start, c.bar_fine]), [commesseConDate])
  const range = useMemo(() => calcRange(allDates), [allDates])
  const monthTicks = useMemo(() => range ? buildMonthTicks(range.minDate, range.maxDate) : [], [range])
  const weekTicks  = useMemo(() => range ? buildWeekTicks(range.minDate, range.maxDate) : [], [range])
  const totalDays  = range ? diffDays(range.minDate, range.maxDate) + 1 : 0
  const totalW     = totalDays * DAY_W
  const todayX     = range ? diffDays(range.minDate, today) * DAY_W : -1

  // Densità: per ogni giorno, quante commesse sono attive
  const densityByWeek = useMemo(() => {
    if (!range) return []
    const weeks: { start: Date; count: number }[] = []
    const numWeeks = Math.ceil(totalDays / 7)
    for (let w = 0; w < numWeeks; w++) {
      const wStart = new Date(range.minDate.getTime() + w * 7 * 86400000)
      const wEnd   = new Date(wStart.getTime() + 6 * 86400000)
      const active = commesseConDate.filter(c => {
        if (!c.bar_start || !c.bar_fine) return false
        const s = parseDate(c.bar_start)
        const e = parseDate(c.bar_fine)
        return s <= wEnd && e >= wStart
      }).length
      weeks.push({ start: wStart, count: active })
    }
    return weeks
  }, [commesseConDate, range, totalDays])

  const maxDensity = Math.max(1, ...densityByWeek.map(w => w.count))

  const syncScroll = () => {
    if (rightRef.current && leftRef.current)
      leftRef.current.scrollTop = rightRef.current.scrollTop
  }

  if (!range || commesse.length === 0)
    return <div className="text-center text-gray-400 py-16">Nessuna commessa con date definite</div>

  return (
    <div className="flex border border-gray-200 rounded-lg overflow-hidden bg-white h-full">
      {/* Left panel */}
      <div className="flex-shrink-0 border-r border-gray-200 flex flex-col" style={{ width: LEFT_W }}>
        {/* Header + density spacer */}
        <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200" style={{ height: HEADER_H + DENSITY_H }}>
          <div className="px-3 flex items-end pb-1" style={{ height: HEADER_H }}>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Commessa</span>
          </div>
          <div className="px-3 flex items-center" style={{ height: DENSITY_H }}>
            <span className="text-xs text-gray-400">Sovrapposizione →</span>
          </div>
        </div>
        {/* Names */}
        <div className="flex-1 overflow-hidden" ref={leftRef}>
          {commesseConDate.map((c, ci) => {
            const color = STATO_COLOR[c.stato] ?? GANTT_COLORS[ci % GANTT_COLORS.length].bar
            return (
              <div
                key={c.id}
                className="flex items-center px-3 border-b border-gray-100 gap-2"
                style={{ height: ROW_H }}
              >
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                <div className="min-w-0">
                  <p className="font-mono text-xs font-bold text-gray-500 truncate">{c.numero_commessa}</p>
                  <p className="text-xs text-gray-700 truncate">{c.titolo}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 overflow-auto" ref={rightRef} onScroll={syncScroll}>
        <div style={{ width: Math.max(totalW, 600), minWidth: '100%' }}>

          {/* Sticky header */}
          <div className="sticky top-0 z-20 bg-gray-50 border-b border-gray-200 overflow-hidden"
               style={{ height: HEADER_H + DENSITY_H }}>
            {/* Month / week labels */}
            <div style={{ height: HEADER_H }}>
              <div className="relative h-6">
                {monthTicks.map((t, i) => (
                  <div key={i} className="absolute top-0 h-full flex items-center border-l border-gray-200 px-1"
                       style={{ left: t.startDay * DAY_W, width: t.days * DAY_W }}>
                    <span className="text-xs font-medium text-gray-600 capitalize whitespace-nowrap truncate">{t.label}</span>
                  </div>
                ))}
              </div>
              <div className="relative h-6 border-t border-gray-100">
                {zoom === 'settimane' && weekTicks.map((t, i) => (
                  <div key={i} className="absolute top-0 h-full flex items-center border-l border-gray-100 px-0.5"
                       style={{ left: t.startDay * DAY_W }}>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{t.label}</span>
                  </div>
                ))}
                {todayX >= 0 && <div className="absolute top-0 h-full w-px bg-red-400" style={{ left: todayX }} />}
              </div>
            </div>

            {/* Density bar */}
            <div className="relative border-t border-gray-200 bg-white" style={{ height: DENSITY_H }}>
              {densityByWeek.map((w, i) => {
                const x = diffDays(range.minDate, w.start) * DAY_W
                const barH = w.count === 0 ? 0 : Math.round((w.count / maxDensity) * (DENSITY_H - 8))
                const pct = Math.round(w.count / maxDensity * 100)
                const barColor = pct < 50 ? '#86efac' : pct < 80 ? '#fbbf24' : '#f87171'
                return (
                  <div
                    key={i}
                    className="absolute bottom-1 flex items-end justify-center"
                    style={{ left: x, width: 7 * DAY_W - 1, height: DENSITY_H - 4 }}
                    title={`${w.count} commesse in parallelo`}
                  >
                    {w.count > 0 && (
                      <>
                        <div className="w-full rounded-sm" style={{ height: barH, background: barColor, minHeight: 3 }} />
                        {w.count > 0 && (
                          <span
                            className="absolute text-gray-600 font-bold"
                            style={{ fontSize: 9, bottom: barH + 2 }}
                          >{w.count}</span>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
              {todayX >= 0 && <div className="absolute top-0 bottom-0 w-px bg-red-300" style={{ left: todayX }} />}
            </div>
          </div>

          {/* Rows */}
          <div className="relative">
            {todayX >= 0 && (
              <div className="absolute top-0 bottom-0 w-px bg-red-300 z-10 pointer-events-none" style={{ left: todayX }} />
            )}
            {monthTicks.map((t, i) => (
              <div key={i} className="absolute top-0 bottom-0 w-px bg-gray-100" style={{ left: t.startDay * DAY_W }} />
            ))}

            {commesseConDate.map((c, ci) => {
              const color = STATO_COLOR[c.stato] ?? GANTT_COLORS[ci % GANTT_COLORS.length].bar
              const noDate = !c.bar_start || !c.bar_fine

              if (noDate) {
                return (
                  <div key={c.id} className="relative border-b border-gray-50 flex items-center px-2" style={{ height: ROW_H }}>
                    <span className="text-xs text-gray-300 italic">date non definite</span>
                  </div>
                )
              }

              const startX = diffDays(range.minDate, parseDate(c.bar_start!)) * DAY_W
              const endX   = diffDays(range.minDate, parseDate(c.bar_fine!)) * DAY_W
              const barW   = Math.max(endX - startX, 12)
              const giorni = c.giorni_totali ?? '?'

              return (
                <div key={c.id} className="relative border-b border-gray-50" style={{ height: ROW_H }}>
                  {/* Background band */}
                  <div
                    className="absolute top-1 bottom-1 opacity-8 rounded"
                    style={{ left: startX, width: barW, background: color, opacity: 0.08 }}
                  />
                  {/* Main bar */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 rounded-md flex items-center px-2 gap-1 overflow-hidden"
                    style={{ left: startX, width: barW, height: ROW_H - 14, background: color }}
                    title={`${c.numero_commessa} — ${c.titolo}\n${c.bar_start} → ${c.bar_fine}\n${giorni} giorni totali`}
                  >
                    {barW > 24 && (
                      <span className="text-white font-mono text-xs font-bold truncate whitespace-nowrap">
                        {c.numero_commessa}
                        {barW > 80 && <span className="font-normal ml-1 opacity-90">{giorni}g</span>}
                      </span>
                    )}
                  </div>
                  {/* Days label above bar if bar is narrow */}
                  {barW <= 80 && barW > 12 && (
                    <div
                      className="absolute text-xs font-bold whitespace-nowrap pointer-events-none"
                      style={{ left: startX + barW + 3, top: '50%', transform: 'translateY(-50%)', color, fontSize: 10 }}
                    >
                      {giorni}g
                    </div>
                  )}
                  {/* Consegna diamond */}
                  {c.data_prevista_consegna && (
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rotate-45 border-2 bg-white z-10"
                      style={{ left: endX - 6, borderColor: color }}
                      title={`Consegna: ${c.data_prevista_consegna}`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
